import { isFieldBinding } from "@redotech/effect-api-shared/default-rules/field-binding";
import type {
  ConditionEvaluationResult,
  ValuesAtExecution,
} from "@redotech/effect-api-shared/models/shopper-ai/rule-engine-results";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  ContactChannel,
  LastOrderProperty,
  ResponseTimeOperator,
  ResponseType,
  TimeBasedMode,
  TimeUnit,
  aiStatusConditionSchema,
  assignmentV3ConditionSchema,
  lastOrderConditionSchema,
  receivedAtConditionSchema,
  responseTimeConditionSchema,
  sentFromConditionSchema,
  ticketChannelV3ConditionSchema,
  ticketStatusV3ConditionSchema,
  timeBasedConditionSchema,
  type AiStatusCondition,
  type AssignmentV3Condition,
  type LastOrderCondition,
  type ReceivedAtCondition,
  type ResponseTimeCondition,
  type RuleV3Condition,
  type SentFromCondition,
  type TicketChannelV3Condition,
  type TicketStatusV3Condition,
  type TimeBasedCondition,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import type {
  HasTagCondition,
  MessageBodyMatchesCondition,
  MessageSentimentCondition,
  SubjectLineCondition,
  TopicCondition,
} from "@redotech/redo-model/advanced-flow/rules/schemas";
import {
  hasTagConditionSchema,
  messageBodyMatchesConditionSchema,
  messageSentimentConditionSchema,
  subjectLineConditionSchema,
  topicConditionSchema,
} from "@redotech/redo-model/advanced-flow/rules/schemas";
import {
  DateQueryOperator,
  MESSAGE_ONLY_CONDITION_TYPES,
  MESSAGE_TRIGGER_TYPES,
  NumericQueryOperator,
  QueryOperator,
  RuleConditionType,
  type RuleTriggerType,
  type SelectionQueryOperator,
  type TextQueryOperator,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useMemo } from "react";
import { AiStatusCondition as AiStatusConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/ai-status-condition";
import { AssignmentCondition as AssignmentConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/assignment-condition";
import { HasTagCondition as HasTagConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/has-tag-condition";
import { LastOrderCondition as LastOrderConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/last-order-condition";
import { MessageBodyMatchesCondition as MessageBodyMatchesConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/message-body-matches-condition";
import { MessageSentimentCondition as MessageSentimentConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/message-sentiment-condition";
import { ReceivedAtCondition as ReceivedAtConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/received-at-condition";
import { ResponseTimeCondition as ResponseTimeConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/response-time-condition";
import { SentFromCondition as SentFromConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/sent-from-condition";
import { SubjectLineCondition as SubjectLineConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/subject-line-condition";
import { TicketChannelV3Condition as TicketChannelV3ConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/ticket-channel-v3-condition";
import { TicketStatusV3Condition as TicketStatusV3ConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/ticket-status-v3-condition";
import { TimeBasedCondition as TimeBasedConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/time-based-condition";
import { TopicCondition as TopicConditionComponent } from "../../flow-editor/advanced-flow-editor/steps/rules/conditions/topic-condition";
import { useDefaultRuleEditContext } from "../default-rule-edit-context";
import {
  dateOperatorOptions,
  getOperatorOptionsForConditionType,
  numericOperatorOptions,
  type AnyOperator,
} from "./condition-item";

const conditionTypeOptions: {
  id: string;
  type: "text";
  text: string;
  value: RuleConditionType;
}[] = [
  {
    id: RuleConditionType.TICKET_CHANNEL,
    type: "text",
    text: "Ticket channel",
    value: RuleConditionType.TICKET_CHANNEL,
  },
  {
    id: RuleConditionType.TICKET_STATUS,
    type: "text",
    text: "Ticket status",
    value: RuleConditionType.TICKET_STATUS,
  },
  {
    id: RuleConditionType.TOPIC,
    type: "text",
    text: "Topic",
    value: RuleConditionType.TOPIC,
  },
  {
    id: RuleConditionType.SENT_FROM,
    type: "text",
    text: "Sent from",
    value: RuleConditionType.SENT_FROM,
  },
  {
    id: RuleConditionType.RECEIVED_AT,
    type: "text",
    text: "Received at",
    value: RuleConditionType.RECEIVED_AT,
  },
  {
    id: RuleConditionType.SUBJECT_LINE,
    type: "text",
    text: "Subject line",
    value: RuleConditionType.SUBJECT_LINE,
  },
  {
    id: RuleConditionType.MESSAGE_BODY_MATCHES,
    type: "text",
    text: "Message body matches",
    value: RuleConditionType.MESSAGE_BODY_MATCHES,
  },
  {
    id: RuleConditionType.TIME_BASED,
    type: "text",
    text: "Time based",
    value: RuleConditionType.TIME_BASED,
  },
  {
    id: RuleConditionType.ASSIGNMENT,
    type: "text",
    text: "Assignment",
    value: RuleConditionType.ASSIGNMENT,
  },
  {
    id: RuleConditionType.LAST_ORDER,
    type: "text",
    text: "Customer last order",
    value: RuleConditionType.LAST_ORDER,
  },
  {
    id: RuleConditionType.MESSAGE_SENTIMENT,
    type: "text",
    text: "Message sentiment",
    value: RuleConditionType.MESSAGE_SENTIMENT,
  },
  {
    id: RuleConditionType.HAS_TAG,
    type: "text",
    text: "Has tag",
    value: RuleConditionType.HAS_TAG,
  },
  {
    id: RuleConditionType.RESPONSE_TIME,
    type: "text",
    text: "Time since last response",
    value: RuleConditionType.RESPONSE_TIME,
  },
  {
    id: RuleConditionType.AI_STATUS,
    type: "text",
    text: "AI handed off",
    value: RuleConditionType.AI_STATUS,
  },
];

export const ConditionItemV3 = memo(function ConditionItemV3({
  condition,
  onChange,
  onDelete,
  triggerType,
  executionResult,
  valuesAtExecution,
  readonly,
}: {
  condition: RuleV3Condition;
  onChange: (condition: RuleV3Condition) => void;
  onDelete: () => void;
  triggerType?: RuleTriggerType;
  executionResult?: ConditionEvaluationResult;
  valuesAtExecution?: ValuesAtExecution;
  readonly?: boolean;
}) {
  // When rendered inside a default-rule edit page the condition structure
  // is fixed by the rule's template. Force readonly on the type/operator/
  // delete controls, and derive per-condition `editableFields` for the
  // leaf editor from `FieldBinding` markers in the matching sub-template.
  const editCtx = useDefaultRuleEditContext();
  const effectiveReadonly = readonly || editCtx !== null;
  const templateCondition =
    editCtx && condition.conditionType
      ? editCtx.findConditionTemplate(condition.conditionType)
      : undefined;
  const editableFields = useMemo<ReadonlySet<string> | undefined>(() => {
    if (!editCtx) return undefined;
    const set = new Set<string>();
    if (templateCondition) {
      for (const [key, value] of Object.entries(templateCondition)) {
        if (isFieldBinding(value)) set.add(key);
      }
    }
    return set;
  }, [editCtx, templateCondition]);

  const filteredConditionTypeOptions = useMemo(() => {
    const isMessageTrigger =
      triggerType &&
      (MESSAGE_TRIGGER_TYPES as readonly string[]).includes(triggerType);
    if (isMessageTrigger) {
      return conditionTypeOptions;
    }
    return conditionTypeOptions.filter(
      (opt) =>
        !(MESSAGE_ONLY_CONDITION_TYPES as readonly string[]).includes(
          opt.value,
        ),
    );
  }, [triggerType]);
  const handleTypeChange = useCallback(
    (newType: RuleConditionType) => {
      let newCondition: RuleV3Condition;

      switch (newType) {
        case RuleConditionType.TICKET_CHANNEL:
          newCondition = {
            conditionType: RuleConditionType.TICKET_CHANNEL,
            channels: [],
            queryOperator: QueryOperator.IS,
          } satisfies TicketChannelV3Condition;
          break;
        case RuleConditionType.TICKET_STATUS:
          newCondition = {
            conditionType: RuleConditionType.TICKET_STATUS,
            statuses: [],
            queryOperator: QueryOperator.IS,
          } satisfies TicketStatusV3Condition;
          break;
        case RuleConditionType.TOPIC:
          newCondition = {
            conditionType: RuleConditionType.TOPIC,
            topics: [],
            queryOperator: QueryOperator.IS,
          } satisfies TopicCondition;
          break;
        case RuleConditionType.SENT_FROM:
          newCondition = {
            conditionType: RuleConditionType.SENT_FROM,
            channel: ContactChannel.EMAIL,
            values: [],
            queryOperator: QueryOperator.CONTAINS,
          } satisfies SentFromCondition;
          break;
        case RuleConditionType.RECEIVED_AT:
          newCondition = {
            conditionType: RuleConditionType.RECEIVED_AT,
            channel: ContactChannel.EMAIL,
            values: [],
            queryOperator: QueryOperator.IS_EXACTLY,
          } satisfies ReceivedAtCondition;
          break;
        case RuleConditionType.SUBJECT_LINE:
          newCondition = {
            conditionType: RuleConditionType.SUBJECT_LINE,
            queryStrings: [],
            caseSensitive: false,
            queryOperator: QueryOperator.CONTAINS,
          } satisfies SubjectLineCondition;
          break;
        case RuleConditionType.MESSAGE_BODY_MATCHES:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_BODY_MATCHES,
            queryStrings: [],
            caseSensitive: false,
            queryOperator: QueryOperator.CONTAINS,
          } satisfies MessageBodyMatchesCondition;
          break;
        case RuleConditionType.TIME_BASED:
          newCondition = {
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.BUSINESS_HOURS,
          } satisfies TimeBasedCondition;
          break;
        case RuleConditionType.ASSIGNMENT:
          newCondition = {
            conditionType: RuleConditionType.ASSIGNMENT,
            mode: { _tag: "type", assignmentTypes: [] },
            queryOperator: QueryOperator.IS,
          } satisfies AssignmentV3Condition;
          break;
        case RuleConditionType.LAST_ORDER:
          newCondition = {
            conditionType: RuleConditionType.LAST_ORDER,
            property: LastOrderProperty.TIME,
            queryOperator: DateQueryOperator.WITHIN_LAST,
            unit: TimeUnit.DAYS,
          } satisfies LastOrderCondition;
          break;
        case RuleConditionType.MESSAGE_SENTIMENT:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_SENTIMENT,
            sentiments: [],
            queryOperator: QueryOperator.IS,
          } satisfies MessageSentimentCondition;
          break;
        case RuleConditionType.HAS_TAG:
          newCondition = {
            conditionType: RuleConditionType.HAS_TAG,
            tags: [],
            queryOperator: QueryOperator.IS,
          } satisfies HasTagCondition;
          break;
        case RuleConditionType.RESPONSE_TIME:
          newCondition = {
            conditionType: RuleConditionType.RESPONSE_TIME,
            responseType: ResponseType.EITHER,
            responseTimeOperator: ResponseTimeOperator.MORE_THAN,
            value: 0,
            unit: TimeUnit.HOURS,
          } satisfies ResponseTimeCondition;
          break;
        case RuleConditionType.AI_STATUS:
          newCondition = {
            conditionType: RuleConditionType.AI_STATUS,
            statuses: ["HANDED_OFF"],
            queryOperator: QueryOperator.IS,
          } satisfies AiStatusCondition;
          break;
        case RuleConditionType.AFTER_HOURS:
        case RuleConditionType.LAST_ORDER_TIME:
        case RuleConditionType.SENT_FROM_EMAIL:
        case RuleConditionType.RECEIVED_AT_EMAIL:
        case RuleConditionType.ASSIGNMENT_TYPE:
          // V2-only conditions
          return;
        default:
          return assertNever(newType);
      }

      onChange(newCondition);
    },
    [onChange],
  );

  const renderConditionDetails = useMemo(() => {
    if (!condition.conditionType) return null;

    const conditionType = condition.conditionType;

    switch (conditionType) {
      case RuleConditionType.TIME_BASED:
        return (
          <TimeBasedConditionComponent
            condition={timeBasedConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.ASSIGNMENT:
        return (
          <AssignmentConditionComponent
            condition={assignmentV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_BODY_MATCHES:
        return (
          <MessageBodyMatchesConditionComponent
            condition={messageBodyMatchesConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.RECEIVED_AT:
        return (
          <ReceivedAtConditionComponent
            condition={receivedAtConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.SENT_FROM:
        return (
          <SentFromConditionComponent
            condition={sentFromConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.SUBJECT_LINE:
        return (
          <SubjectLineConditionComponent
            condition={subjectLineConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.TICKET_CHANNEL:
        return (
          <TicketChannelV3ConditionComponent
            condition={ticketChannelV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.TICKET_STATUS:
        return (
          <TicketStatusV3ConditionComponent
            condition={ticketStatusV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.TOPIC:
        return (
          <TopicConditionComponent
            condition={topicConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.LAST_ORDER:
        return (
          <LastOrderConditionComponent
            condition={lastOrderConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_SENTIMENT:
        return (
          <MessageSentimentConditionComponent
            condition={messageSentimentConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.HAS_TAG:
        return (
          <HasTagConditionComponent
            condition={hasTagConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.RESPONSE_TIME:
        return (
          <ResponseTimeConditionComponent
            condition={responseTimeConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.AI_STATUS:
        return (
          <AiStatusConditionComponent
            condition={aiStatusConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      default:
        assertNever(conditionType);
    }
  }, [condition, onChange, editableFields]);

  const operatorOptions = useMemo(() => {
    if (!condition.conditionType) return null;
    if (condition.conditionType === RuleConditionType.RESPONSE_TIME) {
      return null;
    }
    if (condition.conditionType === RuleConditionType.LAST_ORDER) {
      return condition.property === LastOrderProperty.TIME
        ? dateOperatorOptions
        : numericOperatorOptions;
    }
    return getOperatorOptionsForConditionType(condition.conditionType);
  }, [condition]);

  const handleOperatorChange = useCallback(
    (newOperator: AnyOperator) => {
      switch (condition.conditionType) {
        case RuleConditionType.TIME_BASED:
        case RuleConditionType.RESPONSE_TIME:
          return;
        case RuleConditionType.TICKET_CHANNEL:
        case RuleConditionType.TICKET_STATUS:
        case RuleConditionType.TOPIC:
        case RuleConditionType.ASSIGNMENT:
        case RuleConditionType.MESSAGE_SENTIMENT:
        case RuleConditionType.HAS_TAG:
        case RuleConditionType.AI_STATUS:
          onChange({
            ...condition,
            queryOperator: newOperator as SelectionQueryOperator,
          });
          break;
        case RuleConditionType.SUBJECT_LINE:
        case RuleConditionType.MESSAGE_BODY_MATCHES:
        case RuleConditionType.SENT_FROM:
        case RuleConditionType.RECEIVED_AT:
          onChange({
            ...condition,
            queryOperator: newOperator as TextQueryOperator,
          });
          break;
        case RuleConditionType.LAST_ORDER:
          if (condition.property === LastOrderProperty.TIME) {
            onChange({
              ...condition,
              queryOperator: newOperator as DateQueryOperator,
            });
          } else {
            const numOp = newOperator as NumericQueryOperator;
            if (numOp === NumericQueryOperator.BETWEEN) {
              onChange({
                conditionType: condition.conditionType,
                property: condition.property,
                queryOperator: numOp,
                minValue: 0,
                maxValue: 0,
              });
            } else {
              onChange({
                conditionType: condition.conditionType,
                property: condition.property,
                queryOperator: numOp,
                value: 0,
              });
            }
          }
          break;
        default:
          assertNever(condition);
      }
    },
    [condition, onChange],
  );

  const currentOperator = useMemo(() => {
    if (!condition.conditionType || !operatorOptions) return undefined;
    const operator =
      "queryOperator" in condition ? condition.queryOperator : undefined;
    return operatorOptions.find((opt) => opt.value === operator);
  }, [condition, operatorOptions]);

  return (
    <Flex
      align="flex-start"
      bgColor="secondary"
      dir="column"
      gap="lg"
      p="lg"
      radius="sm"
    >
      <Flex align="center" gap="sm" justify="space-between" w="full">
        <Flex align="center" gap="sm" grow={1}>
          <RedoSingleSelectDropdownInput
            disabled={effectiveReadonly}
            options={filteredConditionTypeOptions}
            optionSelected={({ item }) => handleTypeChange(item.value)}
            placeholder="Select condition"
            selectedItem={
              condition.conditionType
                ? filteredConditionTypeOptions.find(
                    (opt) => opt.value === condition.conditionType,
                  )
                : undefined
            }
          />
          {operatorOptions && (
            <RedoSingleSelectDropdownInput
              disabled={
                // In default-rule edit mode, operator is editable only
                // when the rule's template binds the condition's
                // `queryOperator` field. Otherwise honour the normal
                // readonly flag.
                editCtx !== null
                  ? !editableFields?.has("queryOperator")
                  : readonly
              }
              options={operatorOptions}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              selectedItem={currentOperator}
            />
          )}
        </Flex>
        {!effectiveReadonly && (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={onDelete}
            size="sm"
            theme="destructive"
          />
        )}
      </Flex>

      {condition.conditionType && renderConditionDetails}

      {executionResult && (
        <ExecutionAnnotation
          conditionType={condition.conditionType}
          result={executionResult}
          valuesAtExecution={valuesAtExecution}
        />
      )}
    </Flex>
  );
});

const ExecutionAnnotation = memo(function ExecutionAnnotation({
  conditionType,
  valuesAtExecution,
}: {
  conditionType: RuleConditionType;
  result: ConditionEvaluationResult;
  valuesAtExecution?: ValuesAtExecution;
}) {
  if (!valuesAtExecution) return null;

  switch (conditionType) {
    case RuleConditionType.TICKET_STATUS: {
      const status = valuesAtExecution.conversation?.status;
      if (!status) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }}>
          <Text fontSize="xs" textColor="tertiary">
            Current:
          </Text>
          <RedoBadge color="blue" size="sm" text={status} />
        </Flex>
      );
    }
    case RuleConditionType.TICKET_CHANNEL: {
      const channels = valuesAtExecution.channels?.integrationTypes;
      if (!channels) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }} wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Current:
          </Text>
          {channels.length > 0 ? (
            channels.map((ch) => (
              <RedoBadge color="blue" key={ch} size="sm" text={ch} />
            ))
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              none
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.ASSIGNMENT_TYPE: {
      const assignee = valuesAtExecution.conversation?.assigneeType;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }}>
          <Text fontSize="xs" textColor="tertiary">
            Current:
          </Text>
          <RedoBadge color="blue" size="sm" text={assignee ?? "No assignee"} />
        </Flex>
      );
    }
    case RuleConditionType.SUBJECT_LINE: {
      const subjects = valuesAtExecution.threads?.emailSubjects;
      if (!subjects) return null;
      return (
        <Flex
          align="flex-start"
          dir="column"
          gap="xs"
          style={{ width: "100%" }}
        >
          <Text fontSize="xs" textColor="tertiary">
            Current subjects:
          </Text>
          {subjects.length > 0 ? (
            subjects.map((s, i) => (
              <Text fontSize="xs" key={i} textColor="primary">
                {s}
              </Text>
            ))
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              none
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.HAS_TAG: {
      const tags = valuesAtExecution.conversation?.tags;
      if (!tags) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }} wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Current tags:
          </Text>
          {tags.length > 0 ? (
            tags.map((tag) => (
              <RedoBadge color="blue" key={tag} size="sm" text={tag} />
            ))
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              none
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.TOPIC: {
      const topics = valuesAtExecution.conversation?.topics;
      if (!topics) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }} wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Current topics:
          </Text>
          {topics.length > 0 ? (
            topics.map((topic) => (
              <RedoBadge color="blue" key={topic} size="sm" text={topic} />
            ))
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              none
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.MESSAGE_SENTIMENT: {
      const sentiment = valuesAtExecution.conversation?.sentiment;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }}>
          <Text fontSize="xs" textColor="tertiary">
            Current sentiment:
          </Text>
          <RedoBadge color="blue" size="sm" text={sentiment ?? "unknown"} />
        </Flex>
      );
    }
    case RuleConditionType.MESSAGE_BODY_MATCHES: {
      const body = valuesAtExecution.message?.body;
      if (body === undefined) return null;
      return (
        <Flex
          align="flex-start"
          dir="column"
          gap="xs"
          style={{ width: "100%" }}
        >
          <Text fontSize="xs" textColor="tertiary">
            Message body:
          </Text>
          {body ? (
            <Text fontSize="xs" textColor="primary">
              {body.length > 200 ? `${body.slice(0, 200)}…` : body}
            </Text>
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              (empty)
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.SENT_FROM: {
      const fromAddresses = valuesAtExecution.message?.fromAddresses;
      if (!fromAddresses) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }} wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Sent from:
          </Text>
          {fromAddresses.length > 0 ? (
            fromAddresses.map((addr) => (
              <RedoBadge color="blue" key={addr} size="sm" text={addr} />
            ))
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              none
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.RECEIVED_AT: {
      const toAddresses = valuesAtExecution.message?.toAddresses;
      if (!toAddresses) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }} wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Received at:
          </Text>
          {toAddresses.length > 0 ? (
            toAddresses.map((addr) => (
              <RedoBadge color="blue" key={addr} size="sm" text={addr} />
            ))
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              none
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.RESPONSE_TIME: {
      const lastCustomerAt =
        valuesAtExecution.conversation?.lastCustomerMessageAt;
      const lastMerchantAt =
        valuesAtExecution.conversation?.lastMerchantMessageAt;
      if (lastCustomerAt === undefined && lastMerchantAt === undefined)
        return null;
      return (
        <Flex
          align="flex-start"
          dir="column"
          gap="xs"
          style={{ width: "100%" }}
        >
          {lastCustomerAt !== undefined && (
            <Text fontSize="xs" textColor="tertiary">
              Last customer message:{" "}
              {lastCustomerAt
                ? new Date(lastCustomerAt).toLocaleString()
                : "never"}
            </Text>
          )}
          {lastMerchantAt !== undefined && (
            <Text fontSize="xs" textColor="tertiary">
              Last merchant message:{" "}
              {lastMerchantAt
                ? new Date(lastMerchantAt).toLocaleString()
                : "never"}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.LAST_ORDER: {
      const lastOrder = valuesAtExecution.lastOrder;
      if (!lastOrder) return null;
      return (
        <Flex
          align="flex-start"
          dir="column"
          gap="xs"
          style={{ width: "100%" }}
        >
          {lastOrder.time !== undefined && (
            <Text fontSize="xs" textColor="tertiary">
              Order date: {new Date(lastOrder.time).toLocaleString()}
            </Text>
          )}
          {lastOrder.value !== undefined && (
            <Text fontSize="xs" textColor="tertiary">
              Order total: ${lastOrder.value.toFixed(2)}
            </Text>
          )}
          {lastOrder.itemCount !== undefined && (
            <Text fontSize="xs" textColor="tertiary">
              Item count: {lastOrder.itemCount}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.TIME_BASED: {
      const currentTime = valuesAtExecution.currentTime;
      if (!currentTime) return null;
      return (
        <Flex align="center" gap="xs" style={{ width: "100%" }}>
          <Text fontSize="xs" textColor="tertiary">
            Evaluated at:
          </Text>
          <Text fontSize="xs" textColor="primary">
            {new Date(currentTime).toLocaleString()}
          </Text>
        </Flex>
      );
    }
    case RuleConditionType.AFTER_HOURS:
    case RuleConditionType.LAST_ORDER_TIME:
    case RuleConditionType.SENT_FROM_EMAIL:
    case RuleConditionType.RECEIVED_AT_EMAIL:
    case RuleConditionType.AI_STATUS:
    case RuleConditionType.ASSIGNMENT:
      return null;
    default:
      assertNever(conditionType);
  }
});
